import { Checkbox, FormControlLabel, Stack } from '@mui/material';
import { Flex } from 'components/shared/flex';
import {
  RhfNumberField,
  RhfSelectField,
  RhfTextField,
  RhfAutocompleteSelectField,
} from 'components/shared/form/rhf_input';
import Modal, { ModalState } from 'components/shared/modal';
import ThemedButton from 'components/shared/themed_button';
import { GlobalStateContext } from 'contexts/global_state_context';
import { ExColumn } from 'entities/ex_column';
import { BulkUpdateForm, BulkUpdateInputForm } from 'entities/instrument';
import { InstrumentCategory } from 'entities/instrument_category';
import { Room } from 'entities/room';
import { User } from 'entities/user';
import React, { useContext, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';

type BulkUpdateModalProps = {
  modalState: ModalState;
  instrumentIds: number[];
  users?: User[];
  instrumentCategories?: InstrumentCategory[];
  rooms?: Room[];
  exColumns?: ExColumn[];
  onSubmit?: (data: BulkUpdateForm) => void;
  onComplete?: () => void;
};

export const BulkUpdateModal = (props: BulkUpdateModalProps) => {
  const { classes } = useStyles();
  const globalState = useContext(GlobalStateContext);
  const instrumentInfoAttributeExColumns =
    props.exColumns?.filter(
      (column) =>
        column.extensionClass === 'InstrumentInfoAttribute' && (!column.clientableId || !column.clientableType)
    ) ?? [];

  const form = useForm<BulkUpdateInputForm>({
    defaultValues: {
      maker: '',
      modelNumber: '',
      name: '',
      serialNumber: '',
      manageNumber: '',
      note: '',
      categoryId: '',
      managerId: '',
      roomId: '',
    },
  });

  const [targets, setTargets] = useState<string[]>([]);

  const handleClickCheckbox = (id: string) => {
    const newArray = targets;
    if (!newArray.includes(id)) {
      newArray.push(id);
    } else {
      newArray.splice(newArray.indexOf(id), 1);
    }
    setTargets([...newArray]);
  };

  const onSubmit: SubmitHandler<BulkUpdateInputForm> = (data: BulkUpdateInputForm) => {
    if (targets.length === 0) {
      globalState.setNotificationMessage({ body: '一括更新する項目を選択してください', colorType: 'error' });
      return;
    }

    const values: BulkUpdateForm = {
      targets: targets,
      instrumentIds: props.instrumentIds,
      ...data,
    };
    props.onSubmit && props.onSubmit(values);
    form.reset();
    setTargets([]);
  };

  useEffect(() => {
    if (!props.modalState.open) return;

    // exColumnValuesの初期化
    const defaultValue = instrumentInfoAttributeExColumns.reduce(
      (hash, column) => {
        hash[column.id!] = '';
        return hash;
      },
      {} as Record<string, string>
    );
    form.setValue('exColumnValues', defaultValue);
  }, [props.exColumns, props.modalState.open]);

  const renderExColumnField = (column: ExColumn) => {
    if (!column.id || !column.inputType) return null;

    switch (column.inputType) {
      case 'integer':
      case 'price':
        return (
          <RhfNumberField
            name={`exColumnValues.${column.id}`}
            control={form.control}
            placeholder={column.name}
            disabled={!targets.includes(`ex_column_values.${column.id}`)}
            rules={{
              required:
                targets.includes(`ex_column_values.${column.id}`) && column.required
                  ? '必須項目のため空白にすることはできません'
                  : false,
            }}
          />
        );
      case 'dropdown':
        return (
          <RhfSelectField
            name={`exColumnValues.${column.id}`}
            control={form.control}
            includeBlank
            blankLabel="指定なし"
            items={(column.options || []).map((option) => ({ value: option, label: option }))}
            disabled={!targets.includes(`ex_column_values.${column.id}`)}
            rules={{
              required:
                targets.includes(`ex_column_values.${column.id}`) && column.required
                  ? '必須項目のため空白にすることはできません'
                  : false,
            }}
          />
        );
      case 'string':
      case 'text':
      default:
        return (
          <RhfTextField
            name={`exColumnValues.${column.id}`}
            control={form.control}
            placeholder={column.name}
            disabled={!targets.includes(`ex_column_values.${column.id}`)}
            rules={{
              required:
                targets.includes(`ex_column_values.${column.id}`) && column.required
                  ? '必須項目のため空白にすることはできません'
                  : false,
            }}
          />
        );
    }
  };

  return (
    <Modal
      modalId="bulk-update-modal"
      title="一括更新"
      modalState={props.modalState}
      size="md"
      footer={
        <>
          <ThemedButton onClick={() => props.modalState.setOpen(false)} color="secondary">
            キャンセル
          </ThemedButton>
          <ThemedButton onClick={form.handleSubmit(onSubmit)} color="success">
            一括更新
          </ThemedButton>
        </>
      }
    >
      {props.rooms && props.rooms.length > 0 && (
        <Stack className={classes.formMargin}>
          <label className={classes.formMargin}>部屋</label>
          <Stack className={classes.formMargin}>
            <FormControlLabel
              control={
                <Checkbox checked={targets.includes('room_id')} onChange={() => handleClickCheckbox('room_id')} />
              }
              label="部屋"
            />
            <RhfAutocompleteSelectField
              name="roomId"
              control={form.control}
              disabled={!targets.includes('room_id')}
              placeholder="部屋"
              items={props.rooms.map((room) => {
                return { value: room.id!, label: room.name! };
              })}
              disableWithGray={true}
              rules={{ required: targets.includes('room_id') ? '部屋は必須です' : false }}
            />
            {form.formState.errors.roomId?.message && (
              <span style={{ color: 'red' }}>{form.formState.errors.roomId.message}</span>
            )}
          </Stack>
        </Stack>
      )}

      <Stack className={classes.formMargin}>
        <label className={classes.formMargin}>機器情報</label>

        <Flex className={classes.formMargin} justifyContent="space-between" style={{ gap: 10 }}>
          <Stack flexGrow={1}>
            <FormControlLabel
              control={<Checkbox checked={targets.includes('maker')} onChange={() => handleClickCheckbox('maker')} />}
              label="メーカー"
            />
            <RhfTextField
              name="maker"
              control={form.control}
              disabled={!targets.includes('maker')}
              placeholder="メーカー名"
              rules={{ required: targets.includes('maker') ? 'メーカー名は必須です' : false }}
            />
            {form.formState.errors.maker?.message && (
              <span style={{ color: 'red' }}>{form.formState.errors.maker.message}</span>
            )}
          </Stack>

          <Stack flexGrow={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={targets.includes('model_number')}
                  onChange={() => handleClickCheckbox('model_number')}
                />
              }
              label="型番"
            />
            <RhfTextField
              name="modelNumber"
              control={form.control}
              disabled={!targets.includes('model_number')}
              placeholder="型番"
              rules={{ required: targets.includes('model_number') ? '型番は必須です' : false }}
            />
            {form.formState.errors.modelNumber?.message && (
              <span style={{ color: 'red' }}>{form.formState.errors.modelNumber.message}</span>
            )}
          </Stack>
        </Flex>

        <Stack className={classes.formMargin}>
          <FormControlLabel
            control={<Checkbox checked={targets.includes('name')} onChange={() => handleClickCheckbox('name')} />}
            label="名称"
          />
          <RhfTextField
            name="name"
            control={form.control}
            disabled={!targets.includes('name')}
            placeholder="名称"
            rules={{ required: targets.includes('name') ? '名称は必須です' : false }}
          />
          {form.formState.errors.name?.message && (
            <span style={{ color: 'red' }}>{form.formState.errors.name.message}</span>
          )}
        </Stack>

        {props.users && props.users.length > 0 && (
          <Stack className={classes.formMargin}>
            <FormControlLabel
              control={
                <Checkbox checked={targets.includes('manager_id')} onChange={() => handleClickCheckbox('manager_id')} />
              }
              label="担当者"
            />
            <RhfAutocompleteSelectField
              name="managerId"
              control={form.control}
              disabled={!targets.includes('manager_id')}
              placeholder="担当者を選択"
              items={props.users.map((user) => {
                return { value: user.id!, label: `${user.familyName} ${user.givenName}` };
              })}
              disableWithGray={true}
            />
          </Stack>
        )}

        {props.instrumentCategories && props.instrumentCategories.length > 0 && (
          <Stack className={classes.formMargin}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={targets.includes('category_id')}
                  onChange={() => handleClickCheckbox('category_id')}
                />
              }
              label="機器カテゴリ"
            />
            <RhfAutocompleteSelectField
              name="categoryId"
              control={form.control}
              disabled={!targets.includes('category_id')}
              placeholder="機器カテゴリを選択"
              items={props.instrumentCategories.map((instrumentCategory) => {
                return { value: instrumentCategory.id!, label: instrumentCategory.name! };
              })}
              disableWithGray={true}
            />
          </Stack>
        )}

        <Flex className={classes.formMargin} justifyContent="space-between" style={{ gap: 10 }}>
          <Stack flexGrow={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={targets.includes('serial_number')}
                  onChange={() => handleClickCheckbox('serial_number')}
                />
              }
              label="シリアル番号"
            />
            <RhfTextField
              name="serialNumber"
              control={form.control}
              disabled={!targets.includes('serial_number')}
              placeholder="シリアル番号"
            />
          </Stack>
          <Stack flexGrow={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={targets.includes('manage_number')}
                  onChange={() => handleClickCheckbox('manage_number')}
                />
              }
              label="管理番号"
            />
            <RhfTextField
              name="manageNumber"
              control={form.control}
              disabled={!targets.includes('manage_number')}
              placeholder="管理番号"
            />
          </Stack>
        </Flex>

        <Stack className={classes.formMargin}>
          <FormControlLabel
            control={<Checkbox checked={targets.includes('note')} onChange={() => handleClickCheckbox('note')} />}
            label="機器の説明等"
          />
          <RhfTextField
            name="note"
            control={form.control}
            disabled={!targets.includes('note')}
            placeholder="機器の説明等"
          />
        </Stack>
      </Stack>

      {instrumentInfoAttributeExColumns.length > 0 && (
        <Stack className={classes.formMargin}>
          <label className={classes.formMargin}>製品管理情報</label>
          {instrumentInfoAttributeExColumns.map((column) => (
            <Stack className={classes.formMargin} key={`ex_column-${column.id}`}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={targets.includes(`ex_column_values.${column.id}`)}
                    onChange={() => handleClickCheckbox(`ex_column_values.${column.id}`)}
                  />
                }
                label={column.name}
              />
              <Stack>
                {renderExColumnField(column)}
                {form.formState.errors.exColumnValues?.[column.id!]?.message && (
                  <span style={{ color: 'red' }}>{form.formState.errors.exColumnValues[column.id!]?.message}</span>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </Modal>
  );
};

const useStyles = makeStyles()({
  formMargin: {
    marginBottom: 10,
  },
});
